// --------------------------------------------------------
// ExamsPage - ScreenName
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import { Search } from "@mui/icons-material";
import { Button, CircularProgress, OutlinedInput } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import CareLineDropdown from "../../components/dropdown/CareLineDropdown";
import ErrorArea from "../../components/error/ErrorArea";
import ExamCard from "../../components/ExamCard/ExamCard";
import MarkConsultButton from "../../components/MarkConsultButton/MarkConsultButton";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import {
  GeneralContainer,
  H1,
  Text,
} from "../../components/UI/generalLayout/Layout.styled";
import { Spacer } from "../../components/UI/spacer/spacer";
import Colors from "../../constants/Colors";
import {
  PersonExamService,
  PersonExamType,
} from "../../core/http/service/PersonExam";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import { ILogin } from "../../store/reducers/login";
import { getYearBeforeToday } from "../../utils/date.utils";
import { CompanyListContainer } from "../company/CompanyList.styled";

import {
  ExamsContainers,
  ExamsHeader,
  HeaderContainer,
  SearchContainer,
} from "./Exams.style";

const years = getYearBeforeToday(5);

interface YearLabel {
  nome: string;
  id: string;
}

const yearsLabels = years.map<YearLabel>((value) => ({
  nome: value,
  id: value,
}));

const allValue = [{ nome: "Todos", id: 0 }];

// --------------------------------------------------------
// ExamsPage INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 *
 *
 */

function ExamsPage(): JSX.Element {
  // --------------------------------------------------------
  // ExamsPage PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const { state } = useLocation();
  const { id } = (state as any) ?? {};

  const [search, setSearch] = useState("");
  const [exams, setExams] = useState<PersonExamType[]>([]);
  const [error, setError] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<YearLabel>();
  const [selectedNameSearch, setSelectedNameSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const { userInfo } = useSelector((state: { login: ILogin }) => state.login);

  useEffect(() => {
    const service = new PersonExamService();
    if (userInfo) {
      setLoading(true);
      const selectedAll =
        selectedYear?.nome !== "Todos" ? selectedYear?.nome : "";

      service
        .getExams({
          id: userInfo.id,
          Nome: selectedNameSearch,
          Ano: selectedAll,
        })
        .then(({ data: { data } }) => {
          setExams(data);
        })
        .catch((error) => {
          setExams([]);
          setError([error]);
        })
        .finally(() => setLoading(false));
    }
  }, [selectedNameSearch, userInfo, selectedYear?.nome]);

  const { isMobile } = useResponsiveInfo();

  const onClickSearch = () => {
    setSelectedNameSearch(search);
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <HeaderContainer style={{ marginBottom: "16px" }}>
          <H1 style={{ color: "#0F172A", fontWeight: 700, fontSize: "22px", display: "flex", alignItems: "center", gap: "10px", margin: 0 }}>
            <span style={{ width: 4, height: 22, background: "#0A5190", borderRadius: 2, display: "inline-block" }}></span>
            <span>Meus exames</span>
            <span style={{ background: "#F0F9FF", color: "#0A5190", padding: "2px 10px", borderRadius: "12px", fontSize: "13px", fontWeight: 600 }}>
              {exams.length}
            </span>
          </H1>
          <MarkConsultButton />
        </HeaderContainer>

        <SearchContainer style={{ gap: "16px", marginBottom: "24px" }}>
          <OutlinedInput
            style={{
              borderRadius: "10px",
              width: isMobile ? "100%" : "380px",
              height: "42px",
              background: "#FFFFFF",
              fontSize: "14px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)"
            }}
            id="outlined-adornment-amount"
            endAdornment={
              <Button onClick={onClickSearch} style={{ minWidth: "auto", padding: "6px" }}>
                <Search style={{ color: "#0A5190", fontSize: "20px" }} />
              </Button>
            }
            placeholder="Buscar por nome do exame..."
            value={search}
            onChange={(event: any) => {
              setSearch(event.target.value);
            }}
          />
          <CareLineDropdown
            value={selectedYear}
            onChange={setSelectedYear}
            items={yearsLabels}
            disabled={false}
            backgroundColor="#F8FAFC"
            textColor="#0A5190"
            placeholder="Selecione o ano"
            containerStyle={{
              width: isMobile ? "100%" : "220px",
            }}
            allValue={allValue}
          />
        </SearchContainer>

        <ErrorArea
          errorList={error}
          onCloseAction={() => {
            setError([]);
          }}
        />

        <ExamsContainers>
          <ExamsHeader>
            <Text bold style={{ color: "#64748B", fontSize: "13px" }}>Nome do exame</Text>
            <Text bold style={{ color: "#64748B", fontSize: "13px" }}>Data do resultado</Text>
          </ExamsHeader>

          <CompanyListContainer isMobile={isMobile}>
            {loading ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <CircularProgress size={30} style={{ color: "#0A5190" }} />
              </div>
            ) : exams.length > 0 && id ? (
              exams
                .filter((d: any) => d.id === id)
                .map((data) => {
                  return (
                    <ExamCard
                      key={data.id}
                      data={data}
                      filter={true}
                      onSearchEqual={(value) => {
                        setSelectedNameSearch(value);
                        setSearch(value);
                      }}
                    />
                  );
                })
            ) : exams.length > 0 ? (
              exams.map((data) => {
                return (
                  <ExamCard
                    key={data.id}
                    data={data}
                    onSearchEqual={(value) => {
                      setSelectedNameSearch(value);
                      setSearch(value);
                    }}
                  />
                );
              })
            ) : (
              <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 24px",
                background: "#F8FAFC",
                border: "1px dashed #CBD5E1",
                borderRadius: "12px",
                width: "100%",
                color: "#64748B",
                textAlign: "center",
                margin: "16px 0"
              }}>
                <Search style={{ fontSize: 28, color: "#94A3B8", marginBottom: "8px" }} />
                <span style={{ fontWeight: 600, fontSize: "14.5px", color: "#0F172A" }}>Nenhum exame encontrado</span>
                <span style={{ fontSize: "13px", marginTop: "2px" }}>Não encontramos resultados com os filtros informados.</span>
              </div>
            )}
          </CompanyListContainer>
        </ExamsContainers>
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default ExamsPage;
